import apiClient from '../lib/apiClient';

export const getAutomation = async (channelId: string) => {
  console.log(`Fetching automation for channel: ${channelId}`);
  const  data  = await apiClient.get(`/whatsapp/automation/${channelId}`);
  return data;
};

export const saveAutomation = async (channelId: string, flow: unknown) => {
  console.log(`Saving automation for channel: ${channelId}`, flow);
  const { data } = await apiClient.post(`/whatsapp/automation/${channelId}`, { flow });
  return data;
};

export const getAutomationIA = async (channelId: string) => {
  const data = await apiClient.get(`/whatsapp/automation-ia/${channelId}`);
  return data;
};

export const saveAutomationIA = async (
  channelId: string,
  payload: {
    systemPrompt?: string;
    files?: File[];
    useOwnCredentials?: boolean;
    aiProvider?: string;
    aiModel?: string;
    aiApiKey?: string;
  },
) => {
  const form = new FormData();
  if (payload.systemPrompt !== undefined) form.append('systemPrompt', payload.systemPrompt);
  if (payload.useOwnCredentials !== undefined) form.append('useOwnCredentials', String(payload.useOwnCredentials));
  if (payload.aiProvider) form.append('aiProvider', payload.aiProvider);
  if (payload.aiModel) form.append('aiModel', payload.aiModel);
  if (payload.aiApiKey) form.append('aiApiKey', payload.aiApiKey);
  for (const f of payload.files || []) form.append('files', f);
  const { data } = await apiClient.post(`/whatsapp/automation-ia/${channelId}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const setAutomationIAActive = async (channelId: string, isActive: boolean) => {
  const { data } = await apiClient.post(`/whatsapp/automation-ia/${channelId}/active`, {
    isActive,
  });
  return data;
};

export const getAutomationStatus = async (channelId: string) => {
  const data = await apiClient.get(`/whatsapp/automation/${channelId}/status`);
  return data as unknown as { isActive: boolean } | null;
};

export const setAutomationActive = async (channelId: string, isActive: boolean) => {
  const { data } = await apiClient.post(`/whatsapp/automation/${channelId}/active`, {
    isActive,
  });
  return data;
};

export const getAiProviders = async () => {
  const data = await apiClient.get('/whatsapp/ai-providers');
  return data as unknown as {
    id: number;
    code: string;
    name: string;
    description?: string;
    models: Array<{
      id: number;
      code: string;
      name: string;
      description?: string;
    }>;
  }[];
};

// ===== Webhook Functions =====

export const getWebhookConfig = async (channelId: string) => {
  const data = await apiClient.get(`/whatsapp/automation-ia/${channelId}/webhook`);
  console.log(`Fetched webhook config for channel ${channelId}:`, data);
  return data as unknown as {
    webhookUrl: string | null;
    webhookEnabled: boolean;
    webhookSecret: string | null; // '***' cuando está oculto, o el secret real al crear/rotar
  };
};

export const saveWebhookConfig = async (
  channelId: string,
  webhookUrl: string,
  webhookSecret?: string,
) => {
  console.log(`Saving webhook config for channel ${channelId}:`, { webhookUrl, webhookSecret: !!webhookSecret });
  const data = await apiClient.post(`/whatsapp/automation-ia/${channelId}/webhook`, {
    webhookUrl,
    webhookSecret, // Opcional, se genera automáticamente si no se pasa
  });
  console.log('Webhook config saved:', data);
  return data as unknown as {
    id: number;
    webhookUrl: string;
    webhookSecret: string; // Secret generado o proporcionado
    webhookEnabled: boolean;
  };
};

export const rotateWebhookSecret = async (channelId: string) => {
  const data = await apiClient.patch(`/whatsapp/automation-ia/${channelId}/webhook/rotate-secret`);
  return data as unknown as {
    id: number;
    webhookSecret: string; // Nuevo secret generado
    webhookEnabled: boolean;
  };
};

export const toggleWebhook = async (channelId: string, enabled: boolean) => {
  const data = await apiClient.patch(`/whatsapp/automation-ia/${channelId}/webhook/toggle`, {
    enabled,
  });
  return data as unknown as {
    id: number;
    webhookEnabled: boolean;
  };
};

export const deleteWebhook = async (channelId: string) => {
  const data = await apiClient.delete(`/whatsapp/automation-ia/${channelId}/webhook`);
  console.log(`Webhook deleted for channel ${channelId}:`, data);
  return data;
};

export const getWebhookLogs = async (
  channelId: string,
  limit: number = 50,
  offset: number = 0,
) => {
  const data = await apiClient.get(
    `/whatsapp/automation-ia/${channelId}/webhook/logs?limit=${limit}&offset=${offset}`,
  );
  return data as unknown as {
    logs: Array<{
      id: number;
      sessionId: string;
      dialogId: string;
      success: boolean;
      httpStatusCode: number | null;
      error: string | null;
      executedAt: string;
      durationMs: number;
      requestPayload: any;
    }>;
    total: number;
  };
};

export const removePdfFile = async (channelId: string, fileId: string) => {
  const data = await apiClient.post(
    `/whatsapp/automation-ia/${channelId}/files/${fileId}/delete?confirm=yes`,
  );
  return data;
};

// ===== Chatbot Global Toggle (Backoffice) =====

export const getChatbotEnabled = async () => {
  const data = await apiClient.get('/backoffice/settings/chatbot-enabled');
  return data as unknown as { chatbotEnabled: boolean };
};

export const setChatbotEnabledAdmin = async (enabled: boolean) => {
  const data = await apiClient.put('/backoffice/settings/chatbot-enabled', { enabled });
  return data as unknown as { chatbotEnabled: boolean };
};

export const getChannelChatbotStatus = async (channelId: string | number) => {
  const data = await apiClient.get(`/backoffice/channels/${channelId}/chatbot-status`);
  return data as unknown as {
    linearActive: boolean;
    aiActive: boolean;
    hasSnapshot: boolean;
    snapshotType: 'linear' | 'ai' | null;
  };
};

export const getChatbotEnabledPublic = async (channelId: string | number) => {
  const data = await apiClient.get(`/whatsapp/automation/${channelId}/chatbot-access`);
  return data as unknown as { chatbotEnabled: boolean };
};

export const setChannelChatbotStatus = async (
  channelId: string | number,
  type: 'linear' | 'ai',
  isActive: boolean,
) => {
  const data = await apiClient.put(`/backoffice/channels/${channelId}/chatbot-status`, {
    type,
    isActive,
  });
  return data;
};

export const getChatbotChannels = async (params: { page: number; pageSize: number; search?: string }) => {
  const query = new URLSearchParams();
  query.set('page', String(params.page));
  query.set('pageSize', String(params.pageSize));
  if (params.search) query.set('search', params.search);
  const data = await apiClient.get(`/backoffice/chatbot-channels?${query.toString()}`);
  return data as unknown as {
    items: Array<{ id: number; nameChannel: string | null; phoneChannel: string | null; userId: string; linearActive: boolean; aiActive: boolean; overrideEnabled: boolean }>;
    total: number; page: number; pageSize: number;
  };
};

export const setChannelChatbotOverride = async (channelId: string | number, enabled: boolean) => {
  const data = await apiClient.put(`/backoffice/channels/${channelId}/chatbot-override`, { enabled });
  return data;
};
